/**
 * ScreenHeaderContainer
 *
 * Shared wrapper for the top header bar across screens (To Do, Calendar,
 * Settings). Provides the rounded-bottom-corner + soft shadow treatment
 * that visually separates the header from the scrolling content below,
 * matching the home screen hero's curve.
 *
 * The colored background extends up behind the status bar so the header
 * looks seamless against the notification area (matching the home screen).
 * Screens using this container should NOT include the `top` safe area edge
 * on their outer SafeAreaView.
 */

import { ReactNode } from 'react';
import { Platform, StyleSheet, View, ViewStyle, StyleProp } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Colors } from '@/constants/theme';
import { BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';

type ScreenHeaderContainerProps = {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  /**
   * When true, drop the rounded bottom corners and render a flat-bottom
   * header. The shadow and background still apply. Used by calendar views
   * that want a flat header edge across the full screen width.
   */
  flatBottom?: boolean;
};

export function ScreenHeaderContainer({ children, style, flatBottom = false }: ScreenHeaderContainerProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const insets = useSafeAreaInsets();

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: palette.cardBackground, paddingTop: insets.top },
        flatBottom && styles.flatBottom,
      ]}
    >
      <View style={style}>{children}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderBottomLeftRadius: BorderRadius.xl,
    borderBottomRightRadius: BorderRadius.xl,
    zIndex: 1,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.18,
        shadowRadius: 8,
      },
      android: {
        elevation: 6,
      },
      default: {},
    }),
  },
  flatBottom: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
});
